import { v4 as uuid } from 'uuid';

import { CustomColumnHeader, CustomColumnItem, CustomColumnType, ID, LineItem, LineItemChanges } from 'types';
import { matchesNew } from './matchesNew';

const sortByOrder = (a: CustomColumnItem, b: CustomColumnItem): number => (
  Number(a.new_header_index) - Number(b.new_header_index)
);

const isNewCustomColumnItem = (customColumnItem: CustomColumnItem): boolean => (
  matchesNew(String(customColumnItem.custom_column_header_id)) || matchesNew(String(customColumnItem.id))
);

const getNewCustomColumnItems = (customColumnItemChanges?: Record<ID, CustomColumnItem>): CustomColumnItem[] => {
  const hasLineItemChanges = customColumnItemChanges && Object.keys(customColumnItemChanges)?.length;

  if (!hasLineItemChanges) return [];
  return Object.values(customColumnItemChanges).filter(isNewCustomColumnItem);
};

export const mergeCustomColumnItems = (lineItem: LineItem, lineItemChanges?: Record<ID, LineItemChanges>): CustomColumnItem[] => {
  const hasLineItemChanges = lineItemChanges && Object.keys(lineItemChanges)?.length;
  if (!hasLineItemChanges) return lineItem.custom_column_items || [];

  const customColumnItems: CustomColumnItem[] = lineItem.custom_column_items || [];
  const customColumnItemsChanges: Record<ID, CustomColumnItem> = lineItemChanges[lineItem.id]?.custom_column_items;
  const hasCustomColumnItemsChanges = customColumnItemsChanges && Object.keys(customColumnItemsChanges).length;
  const result: Record<ID, CustomColumnItem> = {};

  if (hasCustomColumnItemsChanges) {
    if (customColumnItems?.length) {
      const changedCustomColumnItems = customColumnItems.map((customColumnItem: CustomColumnItem) => ({
        ...customColumnItem,
        ...customColumnItemsChanges[customColumnItem.id],
      }));

      // Update existing customColumnItems with changes.
      changedCustomColumnItems.forEach((customColumnItem: CustomColumnItem) => {
        result[customColumnItem.id] = { ...result[customColumnItem.id], ...customColumnItem };
      });
    }
    const newCustomColumnItems = getNewCustomColumnItems(customColumnItemsChanges);

    // Add new customColumnItems.
    newCustomColumnItems.forEach((customColumnItem: CustomColumnItem) => {
      result[customColumnItem.id] = { ...result[customColumnItem.id], ...customColumnItem };
    });
  } else if (customColumnItems?.length) {
    customColumnItems.forEach((customColumnItem: CustomColumnItem) => {
      result[customColumnItem.id] = { ...result[customColumnItem.id], ...customColumnItem };
    });
  }

  return Object.values(result).map((customColumnItem: CustomColumnItem) => ({
    ...customColumnItem,
    line_item_id: lineItem.id,
  })).sort(sortByOrder);
};

interface CreateDefaultItemParams {
  columnType: CustomColumnType;
  displayText: string;
  headerId: ID;
  isSupplierVisible: boolean;
  lineItemId: ID;
  order: number;
}

const createDefaultItem = ({
  columnType,
  displayText,
  headerId,
  isSupplierVisible,
  lineItemId,
  order,
}: CreateDefaultItemParams): CustomColumnItem => ({
  column_type: columnType,
  custom_column_header_id: headerId,
  display_text: displayText,
  id: `new-${uuid()}`,
  is_supplier_visible: isSupplierVisible,
  line_item_id: lineItemId,
  new_header_index: order,
  qualifying_equality: '',
});

interface CreateDefaultItemFromHeaderParams {
  displayText?: string;
  header: CustomColumnHeader;
  lineItemId: ID;
}

const createDefaultItemFromHeader = ({
  displayText = '',
  header,
  lineItemId,
}: CreateDefaultItemFromHeaderParams): CustomColumnItem => {
  const {
    column_order_index: order,
    column_type: columnType,
    id: headerId,
    is_supplier_visible: isSupplierVisible,
  } = header;

  return createDefaultItem({
    columnType,
    displayText,
    headerId,
    isSupplierVisible,
    lineItemId,
    order,
  });
};

interface CustomColumnItemUpdatesParams {
  displayText: string;
  headerId: ID;
  headers: CustomColumnHeader[];
  lineItem: LineItem;
}

export const customColumnItemUpdates = ({
  displayText,
  headerId,
  headers,
  lineItem,
}: CustomColumnItemUpdatesParams): Partial<CustomColumnItem> | undefined => {
  const lineItemId = lineItem.id;
  const customColumnItem = lineItem.custom_column_items?.find((cci: CustomColumnItem): boolean => (
    cci?.line_item_id === lineItemId && cci?.custom_column_header_id === headerId
  ));
  if (customColumnItem?.id) {
    return {
      custom_column_header_id: headerId,
      display_text: displayText,
      id: customColumnItem?.id,
      line_item_id: lineItemId,
    };
  }
  const header = headers.find(({ id }: CustomColumnHeader): boolean => id === headerId);

  if (header) {
    return createDefaultItemFromHeader({
      displayText,
      header,
      lineItemId,
    });
  }
  return undefined;
};
